<template>
    <div class="bulk-import-container">
        <div class="instructions">
            <h3>Bulk Import Instructions</h3>
            <p>
                1. Download the template file and fill it with your container data<br />
                2. Upload the completed file (Excel or CSV)<br />
                3. Submit to process the import<br />
                4. Review any validation errors and correct them as needed
            </p>
        </div>

        <div class="divGroup">
            <h4 style="color:#fff;">Step 1: Download Template</h4>
            <DxButton text="Download Template" icon="download" type="default" styling-mode="contained"
                @click="downloadTemplate" />
        </div>

        <div class="divGroup">
            <h4 style="color:#fff;">Step 2: Upload File</h4>
            <DxFileUploader ref="fileUploader" :multiple="false" :allowed-file-extensions="allowedFileTypes"
                :max-file-size="maxFileSize" upload-mode="useForm" select-button-text="Select File"
                label-text="or Drop file here" @value-changed="onFileSelected" @upload-error="onUploadError" />
            <div v-if="selectedFile" class="file-info">
                <span class="file-name">{{ selectedFile.name }}</span>
                <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
            </div>
        </div>

        <!-- General Errors -->
        <div v-if="hasGeneralErrors" class="divGroup error-section">
            <h4>General Errors</h4>
            <p class="error-message">
                The following errors occurred during import:
            </p>
            <ul class="error-list">
                <li v-for="(error, index) in generalErrors" :key="index" class="error-item">
                    {{ error }}
                </li>
            </ul>
        </div>

        <!-- Import Results Grid -->
        <div v-if="hasAllRows" class="divGroup results-section">
            <h4>Import Results</h4>
            <p class="results-message">{{ importResultsMessage }}</p>
            <DxButton v-if="errorFileData" text="Download Error File" icon="download" type="danger"
                styling-mode="contained" @click="downloadErrorFile" class="download-error-btn" />
            <DxDataGrid :data-source="allRows" :show-borders="true" :show-row-lines="true" :show-column-lines="true"
                :column-auto-width="true" :allow-column-resizing="true" :word-wrap-enabled="true"
                :row-alternation-enabled="false" @row-prepared="onRowPrepared">
                <DxColumn data-field="rowNumber" caption="Row #" :width="70" alignment="center" />
                <DxColumn data-field="containerType" caption="Container Type" :width="130" />
                <DxColumn data-field="customer" caption="Customer" :width="120" />
                <DxColumn data-field="operator" caption="Operator" :width="120" />
                <DxColumn data-field="area" caption="Area" :width="100" />
                <DxColumn data-field="containerName" caption="Name" :width="120" />
                <DxColumn data-field="downhole" caption="Downhole" :width="100" />
                <DxColumn data-field="alias" caption="Alias" :width="100" />
                <DxColumn data-field="deviceSerial" caption="Serial Number" :width="120" />
                <DxColumn data-field="deviceType" caption="Device Type" :width="110" />
                <DxColumn data-field="contractType" caption="Contract Type" :width="110" />
                <DxColumn data-field="status" caption="Status" :width="100" cell-template="status-cell"
                    alignment="center" />
                <DxColumn data-field="errorMessage" caption="Error" :width="250" cell-template="error-cell" />
                <template #status-cell="{ data }">
                    <span :class="getStatusBadgeClass(data.value)">{{ data.value }}</span>
                </template>
                <template #error-cell="{ data }">
                    <span v-if="data.value" class="error-text" :title="data.value">{{ data.value }}</span>
                </template>
                <DxExport :enabled="true" :formats="['xlsx']" />
            </DxDataGrid>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
            <DxButton text="Process Import" icon="check" type="success" styling-mode="contained" :disabled="!canSubmit"
                @click="processImport" />
            <DxButton text="Clear" icon="clear" type="normal" styling-mode="outlined" :disabled="isProcessing"
                @click="clearForm" />
            <DxButton text="Cancel" icon="close" type="danger" styling-mode="outlined" :disabled="isProcessing"
                @click="cancelImport" />
        </div>

        <!-- Loading Overlay -->
        <DxLoadPanel :visible="isProcessing" :show-indicator="true" :show-pane="true" :shading="true"
            shading-color="rgba(0,0,0,0.4)" message="Processing bulk import..." />
    </div>
</template>

<script>
import { DxButton } from "devextreme-vue/button";
import DxFileUploader from "devextreme-vue/file-uploader";
import DxDataGrid, { DxColumn, DxExport } from "devextreme-vue/data-grid";
import { DxLoadPanel } from "devextreme-vue/load-panel";
import { confirm } from "devextreme/ui/dialog";
import notify from "devextreme/ui/notify";
import * as XLSX from "xlsx";

export default {
    name: "ContainerBulkImport",
    components: {
        DxButton,
        DxFileUploader,
        DxDataGrid,
        DxColumn,
        DxExport,
        DxLoadPanel,
    },
    emits: ["import-complete", "import-cancelled"],
    data() {
        return {
            maxFileSize: 10 * 1024 * 1024, // 10MB
            allowedFileTypes: [".xlsx", ".csv"],
            selectedFile: null,
            isProcessing: false,
            generalErrors: [],
            allRows: [],
            importResultsMessage: "",
            errorFileData: null,
            errorFileName: null,
        };
    },
    computed: {
        hasGeneralErrors() {
            return this.generalErrors && this.generalErrors.length > 0;
        },
        hasAllRows() {
            return this.allRows && this.allRows.length > 0;
        },
        canSubmit() {
            return this.selectedFile && !this.isProcessing;
        },
    },
    methods: {
        downloadTemplate() {
            const headers = [
                "Container Type",
                "Customer",
                "Operator",
                "Area",
                "Name",
                "Downhole",
                "Alias",
                "Serial Number",
                "Device Type",
                "Contract Type"
            ];

            const exampleRows = [
                ["Injection Tank", "Customer A", "ABC Operator", "North Area", "Site 001", "", "", "SN123456", "Type A", "Monthly"],
                ["Injection Tank", "Customer B", "XYZ Operator", "South Area", "Site 002", "", "", "SN789012", "Type B", "Annual"],
                ["Bulk Tank", "Customer C", "DEF Operator", "East Area", "Site 003", "", "", "SN345678", "Type C", "Quarterly"],
            ];

            const wb = XLSX.utils.book_new();
            const wsData = [headers, ...exampleRows];
            const ws = XLSX.utils.aoa_to_sheet(wsData);

            const range = XLSX.utils.decode_range(ws['!ref']);
            for (let col = range.s.c; col <= range.e.c; col++) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
                if (!ws[cellAddress]) continue;
                ws[cellAddress].s = {
                    fill: { fgColor: { rgb: "4472C4" } },
                    font: { bold: true, color: { rgb: "FFFFFF" } }
                };
            }

            XLSX.utils.book_append_sheet(wb, ws, "Container Import Template");

            const timestamp = new Date().toISOString().replace(/[:.]/g, "-").substring(0, 19);
            XLSX.writeFile(wb, `container_import_template_${timestamp}.xlsx`);

            notify("Template downloaded successfully", "success", 2000);
        },

        onFileSelected(e) {
            const files = e.value;
            if (files && files.length > 0) {
                this.selectedFile = files[0];
                this.generalErrors = [];
                this.allRows = [];
                this.importResultsMessage = "";
                this.errorFileData = null;
                this.errorFileName = null;

                notify(`File selected: ${this.selectedFile.name}`, "info", 2000);
            } else {
                this.selectedFile = null;
            }
        },

        onUploadError(e) {
            const error = e.error;

            if (error) {
                notify(`Upload error: ${error}`, "error", 3000);
            }
        },

        formatFileSize(bytes) {
            if (bytes === 0) return "0 Bytes";
            const k = 1024;
            const sizes = ["Bytes", "KB", "MB", "GB"];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
        },

        async processImport() {
            if (!this.selectedFile) {
                notify("Please select a file to upload", "warning", 2000);
                return;
            }

            const result = await confirm(
                `<p>You are about to import data from:</p>
        <p><b>${this.selectedFile.name}</b></p>
        <p>This will create or update container records.</p>
        <p>Do you want to proceed?</p>`,
                "Confirm Bulk Import"
            );

            if (!result) {
                return;
            }

            this.isProcessing = true;
            this.generalErrors = [];
            this.allRows = [];
            this.importResultsMessage = "";
            this.errorFileData = null;
            this.errorFileName = null;

            try {
                const formData = new FormData();
                formData.append("file", this.selectedFile);

                await this.$store.dispatch("utils/bulkImportSites", formData);
                const results = this.$store.getters["utils/getBulkImportResults"];

                const hasGeneralErrors = results.errors && results.errors.length > 0;
                const hasAllRows = results.allRows && results.allRows.length > 0;

                if (results.errorFileBytes && results.errorFileName) {
                    this.errorFileData = results.errorFileBytes;
                    this.errorFileName = results.errorFileName;
                }

                // Store allRows for the results grid
                if (hasAllRows) {
                    this.allRows = results.allRows;
                    this.importResultsMessage = results.message ||
                        `Processed ${results.recordsProcessed} record(s), created ${results.recordsCreated} new site(s), updated ${results.recordsUpdated} existing site(s).`;
                }

                if (hasGeneralErrors) {
                    this.generalErrors = results.errors || [];
                }

                if (results.success || hasAllRows) {
                    notify(this.importResultsMessage, "success", 4000);
                    this.$emit("import-complete", results);
                } else {
                    notify(results.message || "Import failed. Please try again.", "error", 4000);
                }
            } catch (error) {
                console.error("Bulk import error:", error);
                notify(
                    "An error occurred during import. Please check your file and try again.",
                    "error",
                    4000
                );
            } finally {
                this.isProcessing = false;
            }
        },

        downloadErrorFile() {
            if (!this.errorFileData || !this.errorFileName) {
                notify("Error file not available", "error", 2000);
                return;
            }

            try {
                const binaryString = atob(this.errorFileData);
                const bytes = new Uint8Array(binaryString.length);
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i);
                }

                const blob = new Blob([bytes], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = this.errorFileName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                notify("Error file downloaded successfully", "success", 2000);
            } catch (error) {
                console.error("Error downloading file:", error);
                notify("Failed to download error file", "error", 2000);
            }
        },

        clearForm() {
            this.selectedFile = null;
            this.generalErrors = [];
            this.allRows = [];
            this.importResultsMessage = "";
            this.errorFileData = null;
            this.errorFileName = null;

            if (this.$refs.fileUploader) {
                this.$refs.fileUploader.instance.reset();
            }

            notify("Form cleared", "info", 1000);
        },

        cancelImport() {
            this.$emit("import-cancelled");
        },

        onRowPrepared(e) {
            if (e.rowType === 'data') {
                if (e.data.isSuccess === true) {
                    e.rowElement.style.backgroundColor = '#e8f5e9';
                    e.rowElement.style.color = '#1b5e20';
                } else if (e.data.isSuccess === false) {
                    e.rowElement.style.backgroundColor = '#ffebee';
                    e.rowElement.style.color = '#b71c1c';
                }
            }
        },

        getStatusBadgeClass(status) {
            switch (status) {
                case 'Created':
                    return 'status-badge status-created';
                case 'Updated':
                    return 'status-badge status-updated';
                case 'Error':
                    return 'status-badge status-error';
                default:
                    return 'status-badge';
            }
        },
    },
};
</script>

<style scoped>
.bulk-import-container {
    padding: 20px;
}

.instructions {
    background-color: #f5f5f5;
    padding: 15px;
    margin-bottom: 20px;
    border-radius: 4px;
    border-left: 4px solid #337ab7;
}

.instructions h3 {
    margin-top: 0;
    color: #333;
    font-size: 18px;
}

.instructions p {
    margin-bottom: 0;
    line-height: 1.8;
    color: #555;
}

.divGroup {
    width: 95%;
    border-style: solid;
    border-width: 1px;
    margin: 20px 0;
    border-color: rgb(100, 100, 100);
    border-radius: 6px;
    padding: 20px;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
}

.divGroup h4 {
    margin-top: 0;
    margin-bottom: 15px;
    font-size: 16px;
    color: #333;
}

.file-info {
    margin-top: 15px;
    padding: 10px;
    background-color: #f8f9fa;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.file-name {
    font-weight: 600;
    color: #333;
}

.file-size {
    color: #666;
    font-size: 14px;
}

.error-section {
    border-color: #d9534f;
    background-color: #fff5f5;
}

.download-error-btn {
    margin-bottom: 20px;
}

.error-message {
    color: #d9534f;
    margin-bottom: 15px;
    font-weight: 500;
}

.error-text {
    color: #d9534f;
    font-weight: 500;
}

.error-list {
    margin: 0;
    padding-left: 20px;
}

.error-item {
    color: #d9534f;
    margin-bottom: 8px;
    line-height: 1.5;
}

.action-buttons {
    display: flex;
    gap: 15px;
    margin-top: 20px;
    padding: 20px 0;
}

.action-buttons .dx-button {
    min-width: 150px;
}

.results-section {
    border-color: #17a2b8;
    background-color: #f8f9fa;
}

.results-message {
    color: #333;
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 15px;
    padding: 10px;
    background-color: #e9ecef;
    border-radius: 4px;
}

.status-badge {
    display: inline-block;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
}

.status-created {
    background-color: #28a745;
    color: #ffffff;
}

.status-updated {
    background-color: #007bff;
    color: #ffffff;
}

.status-error {
    background-color: #dc3545;
    color: #ffffff;
}
</style>
